/**
 * YouTube URL 유틸 — 본문(HTML/텍스트)에서 YouTube 링크를 감지해
 * 미리보기 카드(에디터/상세 페이지)에 사용한다.
 *
 * 본문 sanitize 는 iframe 을 제거하고 <a href> 링크만 보존하므로,
 * 저장된 콘텐츠에서 YouTube URL 을 추출해 클라이언트에서 카드로 렌더링한다.
 */

/** YouTube URL 에서 11자리 video id 추출 (아니면 null) */
export function getYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1).split('/')[0];
      return /^[\w-]{11}$/.test(id) ? id : null;
    }
    if (
      host === 'youtube.com' ||
      host === 'm.youtube.com' ||
      host === 'music.youtube.com'
    ) {
      const v = u.searchParams.get('v');
      if (v && /^[\w-]{11}$/.test(v)) return v;
      const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([\w-]{11})/);
      if (m) return m[1];
    }
    return null;
  } catch {
    return null;
  }
}

/** 문자열이 YouTube URL 인지 */
export function isYouTubeUrl(url: string): boolean {
  return getYouTubeId(url) !== null;
}

/** URL 형태 매칭용 — http(s) 로 시작하는 토큰 */
const URL_RE = /https?:\/\/[^\s"'<>)]+/gi;

/**
 * 본문(HTML 또는 텍스트)에서 YouTube URL 목록 추출.
 *  - <a href="..."> 와 평문 URL 모두 감지
 *  - video id 기준 중복 제거, 등장 순서 유지
 *  - 최대 maxCount 개 (기본 5)
 */
export function extractYouTubeUrls(html: string, maxCount = 5): string[] {
  if (!html) return [];
  const found: string[] = [];
  const seen = new Set<string>();

  const push = (raw: string) => {
    const url = raw.replace(/&amp;/g, '&').trim();
    const id = getYouTubeId(url);
    if (!id || seen.has(id)) return;
    seen.add(id);
    found.push(url);
  };

  // href 속성 우선
  const hrefRe = /href=["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = hrefRe.exec(html)) !== null) push(m[1]);

  // 평문 URL 도 보강
  const plain = html.match(URL_RE) ?? [];
  for (const u of plain) push(u);

  return found.slice(0, maxCount);
}
